import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { builds, keyboards, keycaps, switches, plates } from './dataStore';
import { MultiSelectDropdown, SingleSelectDropdown } from './components/Dropdowns';
import { AudioPlayer } from './components/AudioProvider';
import { Button } from './components/Button';

const buildList = Object.values(builds).reverse();
const keyboardList = Array.from(new Set(Object.values(keyboards).map((kbd) => kbd.name)));
const keycapList = Object.values(keycaps).sort((a, b) => a.name.localeCompare(b.name));
const switchList = Object.values(switches).sort((a, b) => a.name.localeCompare(b.name));
const plateList = Object.values(plates);

function DatabasePage() {
	const [selectedKeyboards, setSelectedKeyboards] = useState([]);
	const [selectedKeycaps, setSelectedKeycaps] = useState([]);
	const [selectedSwitches, setSelectedSwitches] = useState([]);
	const [selectedPlates, setSelectedPlates] = useState([]);
	const [hasFoam, setHasFoam] = useState('all'); 
	const [visibleCount, setVisibleCount] = useState(6);

	const filteredBuilds = buildList.filter((build) => {
		const foamPresent = build.foam && build.foam.length > 0;
		if(hasFoam === 'yes' && !foamPresent) return false;
		if(hasFoam === 'no' && foamPresent) return false;
		const keyboardName = keyboards[build.keyboardId]?.name;
		return (
			(selectedKeyboards.length === 0 || selectedKeyboards.includes(keyboardName)) &&
			(selectedKeycaps.length === 0 || selectedKeycaps.includes(build.keycapId)) &&
			(selectedSwitches.length === 0 || selectedSwitches.includes(build.switchId)) &&
			(selectedPlates.length === 0 || selectedPlates.includes(build.plateId))
		);
	});
	const visibleBuilds = filteredBuilds.slice(0, visibleCount);
	const hasMore = visibleCount < filteredBuilds.length;



	return (
		<div className='min-h-screen bg-[#111112] py-24 px-4 relative overflow-hidden'>
			{/* Ambient blurred backdrop */}
			<div
				aria-hidden='true'
				className='pointer-events-none select-none fixed inset-0 w-full h-full z-0'
				style={{
					backgroundImage: "url('/pictures/database_bg.JPG')",
					backgroundSize: 'cover',
					backgroundPosition: 'center',
					filter: 'blur(12px) brightness(0.5)',
					WebkitFilter: 'blur(12px) brightness(0.5)',
					zIndex: 0,
				}}
			/>
			{/* Main content */}
			<div className='relative z-10'>
				<h1 className='text-3xl font-bold text-white mb-6 text-center drop-shadow'>Build Database</h1>
				<p className='text-gray-300 mb-12 text-center text-base max-w-2xl mx-auto'>
					Browse and filter all keyboard builds, their specs, and sound test data from the collection. Click on the board, keycaps, or switches to see more details about each component.
				</p>
				<div className='flex flex-wrap gap-6 justify-center mb-10'>
					<MultiSelectDropdown label='All Keyboards' options={keyboardList} selected={selectedKeyboards} setSelected={setSelectedKeyboards} />
					<MultiSelectDropdown label='All Keycaps' options={keycapList} selected={selectedKeycaps} setSelected={setSelectedKeycaps} />
					<MultiSelectDropdown label='All Switches' options={switchList} selected={selectedSwitches} setSelected={setSelectedSwitches} />
					<MultiSelectDropdown label='All Plates' options={plateList.map((p) => ({...p, name: p.material}))} selected={selectedPlates} setSelected={setSelectedPlates} />
					<SingleSelectDropdown label='Foam' options={[
						{ id: 'all', name: 'Any Foam' },
						{ id: 'yes', name: 'Has Foam' },
						{ id: 'no', name: 'No Foam' }
					]} selected={hasFoam} setSelected={setHasFoam} />
				</div>
				<div className='grid gap-8 md:grid-cols-3 sm:grid-cols-2 grid-cols-1 mt-8'>
					{visibleBuilds.length === 0 && (
						<p className='text-gray-400 col-span-full text-center text-lg animate-pulse'>No builds match the selected filters.</p>
					)}
					{visibleBuilds.map((build) => {
						const kbd = keyboards[build.keyboardId];
						const kc = keycaps[build.keycapId];
						const sw = switches[build.switchId];
						const pl = plates[build.plateId];
						return (
							<div key={build.id} className='bg-black/80 border border-gray-800 shadow-xl rounded-lg p-6 flex flex-col gap-2 hover:scale-105 transition-transform duration-300'>
								<div className='w-fit'>
									<Link 
										to={`/articles/${kbd.articleid}`}
										className='relative inline-block text-white after:absolute after:left-0 after:-bottom-1 after:w-0 after:h-[2px] after:bg-white after:transition-all after:duration-300 hover:after:w-full transition px-0'
									>
										<h2 className='text-xl font-bold text-white mb-1'>{kbd.name}</h2>
									</Link>
								</div>
								
								<p className='text-gray-300 text-base'>
									<span className='font-semibold'>Color:</span> {kbd?.color || '-'}<br/>
									<span className='font-semibold'>Keycaps:</span>{' '}
									{kc ? (
										<div className='w-fit inline-block'>
											<Link 
												to={`/articles/${kc.articleid}`}
												className='relative inline-block text-gray-200 after:absolute after:left-0 after:-bottom-1 after:w-0 after:h-[2px] after:bg-gray-200 after:transition-all after:duration-300 hover:after:w-full transition px-0'
											>
												<span className='relative z-10'>{kc.name}</span>
											</Link>
										</div>
									) : '-'}<br/>
									<span className='font-semibold'>Switches:</span>{' '}
									{sw ? (
										<div className='w-fit inline-block'>
											<Link 
												to={`/articles/${sw.articleid}`}
												className='relative inline-block text-gray-200 after:absolute after:left-0 after:-bottom-1 after:w-0 after:h-[2px] after:bg-gray-200 after:transition-all after:duration-300 hover:after:w-full transition px-0'
											>
												<span className='relative z-10'>{sw.name}</span>
											</Link>
										</div>
									) : '-'}<br/>
									<span className='font-semibold'>Plate:</span> {pl?.material || '-'}<br/>
									<span className='font-semibold'>Foam:</span> {build.foam && build.foam.length > 0 ? build.foam.join(' + ') : 'No foam'}<br/>
									<span className='font-semibold'>Mount:</span> {build.mount || '-'}<br/>
									<span className='font-semibold'>Sound Test:</span> {build.hasSoundtest ? 'Yes' : 'No'}
								</p>
								{/* Embedded audio player(s) if present */}
								{build.audio && build.audio !== "" && (
									<div className='flex flex-col gap-2 mt-2'>
										<AudioPlayer src={build.audio} className='w-full' />
									</div>
								)}
							</div>
						);
					})}
				</div>
				{hasMore && (
					<div className='flex justify-center mt-8'>
						<Button onClick={() => setVisibleCount((c) => c + 6)} children="Load More" className="text-white font-semibold px-6 py-2" />
					</div>
				)}
			</div>
		</div>
	);
}

export default DatabasePage;
